"use client";

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

type TabsContextValue = { value: string; setValue: (value: string) => void };
const TabsContext = createContext<TabsContextValue | null>(null);

export type TabsProps = {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
};

export function Tabs({ defaultValue = "", value: controlledValue, onValueChange, children, className }: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = controlledValue ?? internalValue;
  const context = useMemo(
    () => ({
      value,
      setValue: (next: string) => {
        if (controlledValue === undefined) setInternalValue(next);
        onValueChange?.(next);
      },
    }),
    [controlledValue, onValueChange, value],
  );
  return <TabsContext.Provider value={context}><div className={cn("w-full", className)}>{children}</div></TabsContext.Provider>;
}

export function TabList({ className, children, variant = "pill", ariaLabel }: { className?: string; children: ReactNode; variant?: "pill" | "segment"; ariaLabel?: string }) {
  return (
    <div
      className={cn(variant === "segment" ? "segment-tabs" : "flex flex-wrap gap-ds-1 rounded-card border border-card-border bg-surface-primary p-ds-1 shadow-raised", className)}
      role="tablist"
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
}

export type TabTriggerProps = { value: string; children: ReactNode; className?: string; disabled?: boolean };

export function TabTrigger({ value, children, className, disabled }: TabTriggerProps) {
  const context = useContext(TabsContext);
  if (!context) throw new Error("TabTrigger must be inside Tabs");
  const active = context.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      className={cn("segment-tab disabled:pointer-events-none disabled:opacity-45", active && "segment-tab-active", className)}
      onClick={() => context.setValue(value)}
    >
      {children}
    </button>
  );
}

export function TabPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  const context = useContext(TabsContext);
  if (!context) throw new Error("TabPanel must be inside Tabs");
  if (context.value !== value) return null;
  return <div className={cn("pt-ds-6", className)} role="tabpanel">{children}</div>;
}
